import { Box, Typography, Divider } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import {
  BOOSTER_POWER_PRICE_USD,
  BOOSTER_STARTER_PRICE_USD,
  SUB_ANNUAL_PRO_PRICE_USD,
  SUB_MONTHLY_PRICE_USD,
  SUB_BASIC_MONTHLY_PRICE_USD,
  SUB_SEASON_PASS_TIER_1_PRICE_USD,
} from "src/config/products.config";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { mobile, isMobileWebBrowser } from "src/utils/platformUtils";

interface TierData {
  tierName: string;
  tierPrice: string;
  tierColor: string;
  reasonTitle: string;
  reasonDescription: string;
  tradeoffTitle: string;
  tradeoffDescription: string;
}

// Helper: format price per credit from price and credits (aligned with ManageMembership)
function getPricePerCredit(priceDollars: number, credits: number, unit: "CREDIT" | "EA" = "CREDIT"): string {
  if (credits <= 0) return unit === "EA" ? "$ -/EA" : "$ - / CREDIT";
  const perCredit = priceDollars / credits;
  // Truncate to 3 decimal places (do not round)
  // const truncated = Math.floor(perCredit * 1000) / 1000;
  // const formatted = truncated.toFixed(3);
  const formatted =
    perCredit >= 0.01 ? perCredit.toFixed(3) : perCredit >= 0.001 ? perCredit.toFixed(3) : `~${perCredit.toFixed(3)}`;
  if (unit === "EA") return `$${formatted}/EA`;
  return `$${formatted} / CREDIT`;
}

const tableHeaders: string[] = ["TIER", "THE REASON TO CHOOSE", "THE REALITY (TRADE-OFFS)"];

function buildPricingTableData(monthlyCredits: number): TierData[] {
  const basicMonthlyUsd = frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_PRICE", SUB_BASIC_MONTHLY_PRICE_USD);
  const basicCredits = frontEnvNumber("REACT_APP_SUB_BASIC_MONTHLY_CREDITS", 100);
  const monthlyUsd = frontEnvNumber("REACT_APP_SUB_MONTHLY_PRICE", SUB_MONTHLY_PRICE_USD);
  const annualUsd = frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_PRICE", SUB_ANNUAL_PRO_PRICE_USD);
  const annualCred = frontEnvNumber("REACT_APP_SUB_ANNUAL_PRO_CREDITS", 12000);
  const boosterStarterCred = frontEnvNumber("REACT_APP_BOOSTER_STARTER_CREDITS", 30);
  const boosterPowerCred = frontEnvNumber("REACT_APP_BOOSTER_POWER_CREDITS", 250);
  const boosterStarterUsd = frontEnvNumber("REACT_APP_BOOSTER_STARTER_PRICE", BOOSTER_STARTER_PRICE_USD);
  const boosterPowerUsd = frontEnvNumber("REACT_APP_BOOSTER_POWER_PRICE", BOOSTER_POWER_PRICE_USD);
  return [
    {
      tierName: "FREE",
      tierPrice: "$0.00",
      tierColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
      reasonTitle: "Zero Friction",
      reasonDescription: "Fundamental tools for trade evaluation and waiver analysis. Get immediate value without spending a dime.",
      tradeoffTitle: "Limited Scope",
      // tradeoffDescription: "Ad-supported access. Smaller context windows, shorter responses, and no audio/voice features.",
      tradeoffDescription: "Smaller context windows, shorter responses, and no audio/voice features.",

    },
    {
      tierName: "BASIC MONTHLY",
      tierPrice: getPricePerCredit(basicMonthlyUsd, basicCredits),
      tierColor: "#4a7bc6",
      reasonTitle: "Good for One League",
      reasonDescription: "Full access to Advanced Reasoning without long-term contracts. Perfect for testing the edge.",
      tradeoffTitle: "Highest Cost",
      tradeoffDescription: "Highest subscription price per credit. Requires manual monthly management to maximize efficiency.",
    },
    {
      tierName: "MONTHLY",
      tierPrice: getPricePerCredit(monthlyUsd, monthlyCredits),
      tierColor: "#22D3EE",
      reasonTitle: "Good for 2-5 Leagues",
      reasonDescription: "Full access to Advanced Reasoning without long-term contracts. Perfect for testing the edge.",
      tradeoffTitle: "Higher Cost",
      tradeoffDescription: "Higher subscription price per credit. Requires manual monthly management to maximize efficiency.",
    },
    //COMMENTED OUT FOR NOW THE SEASON PASS (ALSO IN MANAGEMEMBERSHIP.TSX and TABLEPRICING.TSX) BECAUSE ITS WORKING IN STRIPE, NOT IN APPLE/GOOGLE PAY
    // {
    //   tierName: "SEASON PASS",
    //   tierPrice: getPricePerCredit(SUB_SEASON_PASS_TIER_1_PRICE_USD, Number(process.env.REACT_APP_SUB_SEASON_PASS_TIER_1_CREDITS) || 2400),
    //   tierColor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
    //   reasonTitle: "Strategic Dominance.",
    //   reasonDescription: "Built for the NFL schedule. Great for redraft regulars who need the edge during the active season.",
    //   tradeoffTitle: "Seasonal Window.",
    //   tradeoffDescription: "Premium access ends Aug 1st. Off-season usage automatically reverts to ad-supported Free plan features.",
    // },
    {
      tierName: "ANNUAL",
      tierPrice: getPricePerCredit(annualUsd, annualCred),
      tierColor: "#9333ea",
      reasonTitle: "Good for 5+ Leagues",
      reasonDescription: "Our absolute lowest price per credit. Full year-round support for Dynasty, Rookie drafts, and trade markets.",
      tradeoffTitle: "Upfront Outlay",
      tradeoffDescription: "Requires one annual payment. Designed for serious players committed to 12 months of high-level competition.",
    },
    {
      tierName: "Boosters",
      tierPrice: `${getPricePerCredit(boosterPowerUsd, boosterPowerCred, "EA")} - ${getPricePerCredit(boosterStarterUsd, boosterStarterCred, "EA")}`,
      tierColor: "#188ece",
      reasonTitle: "Pure Utility",
      reasonDescription: "No recurring billing or subscription management. Instant refills for critical weeks or high-stakes trade windows.",
      tradeoffTitle: "Premium Retail",
      tradeoffDescription: "The most expensive way to access our platform.",
    },
  ];
}

const TablePricing = () => {
  const isMobile = isMobileWebBrowser() || mobile;
  const [rtBump, setRtBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setRtBump((x) => x + 1)), []);
  const monthlyCredits = frontEnvNumber("REACT_APP_SUB_MONTHLY_CREDITS", 400);
  const pricingTableData = useMemo(() => buildPricingTableData(monthlyCredits), [monthlyCredits, rtBump]);
  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        mt: 2,
        mb: 2,
        // In desktop, expand beyond parent container using viewport width
        // "@media (min-width: 1025px)": {
        //   width: "100vw",
        //   maxWidth: "none",
        //   marginLeft: "calc(-50vw + 50%)",
        //   marginRight: "calc(-50vw + 50%)",
        // },
      }}
    >
      <Box
        sx={{
          minWidth: isMobile ? "300px" : "500px",
          width: "100%",
          maxWidth: { xs: "100%", md: "100%" },
          margin: { xs: "0", md: "0 auto" },
          bgcolor: "#151921",
          borderRadius: 2,
          border: "1px solid rgba(255, 255, 255, 0.2)",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            display: "flex",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            bgcolor: "rgba(255, 255, 255, 0.05)",

          }}
        >
          {tableHeaders.map((header, index) => (
            <Box
              key={index}
              sx={{
                flex: isMobile && index === 0 ? 0.7 : 1,
                p: isMobile ? 1 : 2,
                borderRight: index < tableHeaders.length - 1 ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
                display: "flex",
                justifyContent: isMobile ? "center" : "left",
                alignItems: isMobile ? "center" : "left",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "bold",
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  textTransform: "uppercase",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                {header}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Table Rows */}
        {pricingTableData.map((tier, index) => (
          <Box key={index}>
            <Box
              sx={{
                display: "flex",
                borderBottom: index < pricingTableData.length - 1 ? "1px solid rgba(255, 255, 255, 0.2)" : "none",

              }}
            >
              {/* TIER Column */}
              <Box
                sx={{
                  flex: isMobile ? 0.725 : 1,
                  p: isMobile ? 0.5 : 2,
                  borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: isMobile ? "14px" : "20px",
                    fontWeight: "bold",
                    color: tier.tierColor,
                    textTransform: "uppercase",
                    fontStyle: "italic",
                    textAlign: isMobile ? "center" : "left",
                  }}
                >
                  {tier.tierName}
                </Typography>
                {/* <Typography
                  variant="body2"
                  sx={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    color:
                      tier.tierName === "FREE"
                        ? "#64748b"
                        : tier.tierName === "ROTOGPT MONTHLY"
                          ? "#64748b"
                          : tier.tierName === "SEASON PASS"
                            ? "#fb923c80"
                            : tier.tierName === "ROTOGPT ANNUAL"
                              ? "#c084fc80"
                              : tier.tierName === "Boosters"
                                ? "#0E5A7A"
                                : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  }}
                >
                  {tier.tierPrice}
                </Typography> */}
              </Box>

              {/* THE REASON TO CHOOSE Column */}
              <Box
                sx={{
                  flex: 1,
                  p: isMobile ? 0.5 : 2,
                  borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "bold",
                    color: "#4ade80",
                  }}
                >
                  {tier.reasonTitle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    lineHeight: 1.5,
                  }}
                >
                  {tier.reasonDescription}
                </Typography>
              </Box>

              {/* THE REALITY (TRADE-OFFS) Column */}
              <Box
                sx={{
                  flex: 1,
                  p: isMobile ? 0.5 : 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "bold",
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  }}
                >
                  {tier.tradeoffTitle}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    lineHeight: 1.5,
                  }}
                >
                  {tier.tradeoffDescription}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TablePricing;
